/**
 * EnergyCheckIn - Check-in diario de energia
 *
 * Basado en evidencia:
 * - Los estados cognitivos fluctuan dia a dia
 * - Adaptar la carga de trabajo al nivel de energia actual
 * - Sin juicio - es informacion, no evaluacion
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Battery, BatteryLow, BatteryMedium, BatteryFull, Zap, X } from 'lucide-react';
import { cn } from '@/core/utils';
import { Button, Card } from '@/ui/primitives';
import { useUIStore } from '@/store';
import { feedbackSuccess } from '@/services/feedback';

type EnergyLevel = 1 | 2 | 3 | 4 | 5;

interface EnergyData {
  level: EnergyLevel;
  date: string;
  notes?: string;
}

const ENERGY_LABELS: Record<EnergyLevel, { label: string; description: string; color: string; icon: typeof Battery }> = {
  1: {
    label: 'Muy bajo',
    description: 'Solo lo esencial hoy. Modo supervivencia activado.',
    color: 'text-rose-400 bg-rose-900/30 border-rose-700',
    icon: BatteryLow,
  },
  2: {
    label: 'Bajo',
    description: 'Dia tranquilo. Prioriza las tareas criticas.',
    color: 'text-amber-400 bg-amber-900/30 border-amber-700',
    icon: BatteryLow,
  },
  3: {
    label: 'Normal',
    description: 'Energia promedio. Sigue tu rutina habitual.',
    color: 'text-slate-300 bg-slate-800/50 border-slate-700',
    icon: BatteryMedium,
  },
  4: {
    label: 'Bueno',
    description: 'Buen dia. Puedes abordar tareas mas exigentes.',
    color: 'text-emerald-400 bg-emerald-900/30 border-emerald-700',
    icon: BatteryFull,
  },
  5: {
    label: 'Excelente',
    description: 'Energia alta. Aprovecha para tareas de crecimiento.',
    color: 'text-blue-400 bg-blue-900/30 border-blue-700',
    icon: Zap,
  },
};

// Key para localStorage
const ENERGY_STORAGE_KEY = 'exocortex-energy-checkin';

function getTodayKey(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

function getStoredEnergy(): EnergyData | null {
  try {
    const stored = localStorage.getItem(ENERGY_STORAGE_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored) as EnergyData;
    // Solo devolver si es de hoy
    if (data.date === getTodayKey()) {
      return data;
    }
    return null;
  } catch {
    return null;
  }
}

function storeEnergy(data: EnergyData): void {
  try {
    localStorage.setItem(ENERGY_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignorar errores de storage
  }
}

interface EnergyCheckInProps {
  onComplete?: (level: EnergyLevel) => void;
}

export function EnergyCheckIn({ onComplete }: EnergyCheckInProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<EnergyLevel | null>(null);
  const [todayEnergy, setTodayEnergy] = useState<EnergyData | null>(null);
  const hardDayMode = useUIStore((state) => state.hardDayMode);
  const setHardDayMode = useUIStore((state) => state.setHardDayMode);

  // Cargar energia guardada al montar
  useEffect(() => {
    const stored = getStoredEnergy();
    setTodayEnergy(stored);

    // Si no hay check-in de hoy, mostrar el modal despues de un delay
    if (!stored) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 2000); // 2 segundos despues de cargar
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSelectLevel = (level: EnergyLevel) => {
    setSelectedLevel(level);
  };

  const handleConfirm = () => {
    if (!selectedLevel) return;

    const data: EnergyData = {
      level: selectedLevel,
      date: getTodayKey(),
    };

    storeEnergy(data);
    setTodayEnergy(data);
    setIsOpen(false);
    feedbackSuccess();

    // Si energia es muy baja, sugerir modo dia dificil
    if (selectedLevel <= 2 && !hardDayMode) {
      setHardDayMode(true);
    }

    onComplete?.(selectedLevel);
  };

  const handleDismiss = () => {
    setIsOpen(false);
  };

  const handleReopen = () => {
    setIsOpen(true);
    setSelectedLevel(todayEnergy?.level ?? null);
  };

  // Mini indicador cuando ya se completo el check-in
  if (todayEnergy && !isOpen) {
    const config = ENERGY_LABELS[todayEnergy.level];
    const Icon = config.icon;

    return (
      <button
        onClick={handleReopen}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
          config.color,
          'hover:opacity-80'
        )}
        title="Click para cambiar"
      >
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium">Energia: {config.label}</span>
      </button>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleDismiss}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md max-h-[90vh] flex flex-col"
          >
            <Card className="p-6 flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-100">
                    ¿Como te sientes hoy?
                  </h2>
                  <p className="text-sm text-slate-400 mt-1">
                    No es un juicio. Es informacion para adaptar tu dia.
                  </p>
                </div>
                <button
                  onClick={handleDismiss}
                  className="p-1 rounded hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Energy levels - scrollable */}
              <div className="flex-1 overflow-y-auto space-y-2 mt-6 -mx-2 px-2">
                {([1, 2, 3, 4, 5] as EnergyLevel[]).map((level) => {
                  const config = ENERGY_LABELS[level];
                  const Icon = config.icon;
                  const isSelected = selectedLevel === level;

                  return (
                    <button
                      key={level}
                      onClick={() => handleSelectLevel(level)}
                      className={cn(
                        'w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all',
                        'text-left',
                        isSelected
                          ? config.color
                          : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                      )}
                    >
                      <Icon className={cn(
                        'w-6 h-6 flex-shrink-0',
                        isSelected ? '' : 'text-slate-500'
                      )} />
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          'font-medium',
                          isSelected ? '' : 'text-slate-300'
                        )}>
                          {config.label}
                        </p>
                        <p className={cn(
                          'text-xs mt-0.5',
                          isSelected ? 'opacity-80' : 'text-slate-500'
                        )}>
                          {config.description}
                        </p>
                      </div>
                      {/* Visual indicator */}
                      <div className="flex gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div
                            key={i}
                            className={cn(
                              'w-2 h-2 rounded-full',
                              i < level
                                ? isSelected ? 'bg-current' : 'bg-slate-600'
                                : 'bg-slate-800'
                            )}
                          />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Actions - fixed at bottom */}
              <div className="flex-shrink-0 pt-6 mt-4 border-t border-slate-800/50 space-y-3">
                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={handleDismiss}
                    className="flex-1"
                  >
                    Saltar
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleConfirm}
                    disabled={!selectedLevel}
                    className="flex-1"
                  >
                    Confirmar
                  </Button>
                </div>

                {/* Note */}
                <p className="text-2xs text-slate-600 text-center">
                  Puedes cambiar esto en cualquier momento tocando el indicador de energia.
                </p>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
